import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { featureFlagsApi, type FeatureFlagMeta } from '@/services/featureFlagsApi';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Admin panel for managing feature-flag definitions. Lists every flag in
 * the FeatureFlag table, lets the admin create new flags, edit label /
 * description / defaultEnabled, and hard-delete. The stable `key` is
 * immutable on the backend so consumer code references stay valid.
 */
export function FeatureFlagsAdminPage(): JSX.Element {
  const { t } = useTranslation();
  const [flags, setFlags] = useState<FeatureFlagMeta[]>([]);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [loadError, setLoadError] = useState<string | null>(null);

  const [createKey, setCreateKey] = useState('');
  const [createLabel, setCreateLabel] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createDefault, setCreateDefault] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDefault, setEditDefault] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setStatus('loading');
    setLoadError(null);
    try {
      const { flags } = await featureFlagsApi.adminList();
      setFlags(flags);
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      setLoadError(err instanceof Error ? err.message : t('admin.featureFlags.loadError'));
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleCreate(e: FormEvent): Promise<void> {
    e.preventDefault();
    setCreateBusy(true);
    setCreateError(null);
    try {
      await featureFlagsApi.adminCreate({
        key: createKey.trim(),
        label: createLabel.trim(),
        description: createDescription.trim(),
        defaultEnabled: createDefault,
      });
      setCreateKey('');
      setCreateLabel('');
      setCreateDescription('');
      setCreateDefault(false);
      await refresh();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t('admin.featureFlags.createError'));
    } finally {
      setCreateBusy(false);
    }
  }

  function startEdit(flag: FeatureFlagMeta): void {
    setEditingId(flag.id);
    setEditLabel(flag.label);
    setEditDescription(flag.description);
    setEditDefault(flag.defaultEnabled);
    setEditError(null);
  }

  function cancelEdit(): void {
    setEditingId(null);
    setEditError(null);
  }

  async function handleSaveEdit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!editingId) return;
    setEditBusy(true);
    setEditError(null);
    try {
      await featureFlagsApi.adminUpdate(editingId, {
        label: editLabel.trim(),
        description: editDescription.trim(),
        defaultEnabled: editDefault,
      });
      setEditingId(null);
      await refresh();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : t('admin.featureFlags.updateError'));
    } finally {
      setEditBusy(false);
    }
  }

  async function handleDelete(id: string, key: string): Promise<void> {
    if (typeof window !== 'undefined' && !window.confirm(t('admin.featureFlags.deleteConfirm', { key }))) {
      return;
    }
    setPendingDeleteId(id);
    try {
      await featureFlagsApi.adminDelete(id);
      await refresh();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t('admin.featureFlags.deleteError'));
    } finally {
      setPendingDeleteId(null);
    }
  }

  return (
    <main className="admin-panel">
      <header className="admin-panel__header">
        <h1>{t('admin.featureFlags.title')}</h1>
        <p className="muted">
          {t('admin.featureFlags.blurb')}
        </p>
      </header>

      <section style={{ marginBottom: 32 }}>
        <h2>{t('admin.featureFlags.createTitle')}</h2>
        <form onSubmit={handleCreate} noValidate>
          <div className="field">
            <label htmlFor="ff-create-key">{t('admin.featureFlags.keyField')}</label>
            <input
              id="ff-create-key"
              type="text"
              value={createKey}
              onChange={(e) => setCreateKey(e.target.value)}
              maxLength={64}
              placeholder={t('admin.featureFlags.keyPlaceholder')}
              required
            />
            <small className="muted"><Trans i18nKey="admin.featureFlags.keyHint" components={{ code: <code /> }} /></small>
          </div>
          <div className="field">
            <label htmlFor="ff-create-label">{t('admin.featureFlags.labelField')}</label>
            <input
              id="ff-create-label"
              type="text"
              value={createLabel}
              onChange={(e) => setCreateLabel(e.target.value)}
              maxLength={80}
              placeholder={t('admin.featureFlags.labelPlaceholder')}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="ff-create-description">{t('admin.featureFlags.descriptionField')}</label>
            <textarea
              id="ff-create-description"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder={t('admin.featureFlags.descriptionPlaceholder')}
            />
          </div>
          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={createDefault}
                onChange={(e) => setCreateDefault(e.target.checked)}
              />{' '}
              {t('admin.featureFlags.defaultOn')}
            </label>
          </div>
          <button type="submit" className="btn" disabled={createBusy || createKey.trim().length === 0 || createLabel.trim().length === 0}>
            {createBusy ? t('admin.featureFlags.creating') : t('admin.featureFlags.createButton')}
          </button>
          {createError && <p className="error" role="alert" style={{ marginTop: 12 }}>{createError}</p>}
        </form>
      </section>

      <section>
        <h2>{t('admin.featureFlags.existing', { count: flags.length })}</h2>
        {status === 'loading' && <p className="muted">{t('common.loading')}</p>}
        {status === 'error' && <p className="error" role="alert">{loadError}</p>}
        {status === 'ready' && flags.length === 0 && (
          <p className="muted">{t('admin.featureFlags.empty')}</p>
        )}
        {status === 'ready' && flags.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.featureFlags.colKey')}</th>
                <th>{t('admin.featureFlags.colLabel')}</th>
                <th>{t('admin.featureFlags.colDescription')}</th>
                <th>{t('admin.featureFlags.colDefault')}</th>
                <th>{t('admin.featureFlags.colCreated')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => {
                const isEditing = editingId === flag.id;
                return (
                  <tr key={flag.id}>
                    <td><code>{flag.key}</code></td>
                    {isEditing ? (
                      <>
                        <td>
                          <input
                            type="text"
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            maxLength={80}
                          />
                        </td>
                        <td>
                          <textarea
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            maxLength={500}
                            rows={2}
                          />
                        </td>
                        <td>
                          <label>
                            <input
                              type="checkbox"
                              checked={editDefault}
                              onChange={(e) => setEditDefault(e.target.checked)}
                            />{' '}
                            {t('admin.featureFlags.on')}
                          </label>
                        </td>
                        <td>{new Date(flag.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button type="button" className="btn" disabled={editBusy} onClick={(e) => { void handleSaveEdit(e); }}>
                            {editBusy ? t('common.saving') : t('common.save')}
                          </button>{' '}
                          <button type="button" className="btn-outline" disabled={editBusy} onClick={cancelEdit}>
                            {t('common.cancel')}
                          </button>
                          {editError && (
                            <div className="error" role="alert" style={{ marginTop: 8 }}>{editError}</div>
                          )}
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{flag.label}</td>
                        <td className="muted">{flag.description || '—'}</td>
                        <td>{flag.defaultEnabled ? t('admin.featureFlags.on') : t('admin.featureFlags.off')}</td>
                        <td>{new Date(flag.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button type="button" className="btn-outline" onClick={() => startEdit(flag)}>{t('admin.featureFlags.edit')}</button>{' '}
                          <button
                            type="button"
                            className="btn-outline"
                            disabled={pendingDeleteId === flag.id}
                            onClick={() => { void handleDelete(flag.id, flag.key); }}
                          >
                            {pendingDeleteId === flag.id ? t('admin.featureFlags.deleting') : t('admin.featureFlags.delete')}
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}
